import { useState } from "react";
import {
  useReportsArchive,
  useDailyCollection,
  useNoShows,
  useUpcomingAppts,
  type ArchiveItem,
  type ArchiveResponse,
} from "@/lib/queries";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Printer, ChartColumn, Archive, LoaderCircle } from "lucide-react";
import { fmtINR, fmtDate, fmtDateTime, fmtTime, dateIST } from "@emr/shared/india";
import { ReportPreviewDialog, type ArchiveDocType } from "@/features/reports/ReportPreviewDialog";
import { createDailyCollectionPdf, createNoShowsPdf, createUpcomingApptsPdf } from "@/lib/pdf/operationalReports";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PermissionGate } from "@/components/PermissionGate";

type ArchiveKey = keyof ArchiveResponse;

const ARCHIVE_SECTIONS: { key: ArchiveKey; label: string }[] = [
  { key: "referralLetters", label: "Referral Letters" },
  { key: "medicalCertificates", label: "Medical Certificates" },
  { key: "visits", label: "Visit Summaries" },
  { key: "investigationRequests", label: "Investigation Requests" },
  { key: "prescriptions", label: "Prescriptions" },
  { key: "invoices", label: "Invoices" },
];

export default function ReportsPage() {
  return (
    <PermissionGate perm="reports.view">
      <div className="mx-auto max-w-7xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Reports</h1>
        </div>
        <Tabs defaultValue="archive">
          <TabsList>
            <TabsTrigger value="archive"><Archive size={14} className="mr-1" /> Archive</TabsTrigger>
            <TabsTrigger value="operational"><ChartColumn size={14} className="mr-1" /> Operational</TabsTrigger>
          </TabsList>
          <TabsContent value="archive"><ArchiveTab /></TabsContent>
          <TabsContent value="operational"><OperationalTab /></TabsContent>
        </Tabs>
      </div>
    </PermissionGate>
  );
}

function useDateRangeFilters() {
  // Clock reads live inside the initializers (lazy) — render stays pure and
  // the defaults are computed exactly once per mount.
  const [from, setFrom] = useState(() => dateIST(Date.now() - 30 * 24 * 3600 * 1000));
  const [to, setTo] = useState(() => dateIST());
  return { from, setFrom, to, setTo };
}

function ArchiveTab() {
  const { from, setFrom, to, setTo } = useDateRangeFilters();
  const [type, setType] = useState<ArchiveKey | "all">("all");
  const [patientId, setPatientId] = useState("");
  const { data, isLoading, isFetching } = useReportsArchive({
    from,
    to,
    patientId: patientId || undefined,
    type: type === "all" ? undefined : type,
  });
  const [previewType, setPreviewType] = useState<ArchiveDocType | null>(null);
  const [previewItem, setPreviewItem] = useState<ArchiveItem | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const openPreview = (t: ArchiveDocType, item: ArchiveItem) => {
    setPreviewType(t);
    setPreviewItem(item);
    setPreviewOpen(true);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as ArchiveKey | "all")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All documents</SelectItem>
                  {ARCHIVE_SECTIONS.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Patient ID (UUID)</Label>
              <Input placeholder="Filter by patient UUID" value={patientId} onChange={(e) => setPatientId(e.target.value)} />
            </div>
          </div>
          {isFetching && <div className="mt-2 text-xs text-muted">Refreshing…</div>}
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      ) : !data ? (
        <Card><CardContent className="p-6 text-center text-muted">No data</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {ARCHIVE_SECTIONS.filter((s) => type === "all" || type === s.key).map((section) => {
            const rows = (data[section.key] as ArchiveItem[]) ?? [];
            if (rows.length === 0) return null;
            return (
              <Card key={section.key}>
                <CardContent className="p-0">
                  <div className="flex items-center justify-between p-4 border-b border-cream-200">
                    <h2 className="font-serif text-lg text-maroon-700 dark:text-amber-500">{section.label} ({rows.length})</h2>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Patient</TableHead>
                        <TableHead>UHID</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Preview</TableHead>
                        <TableHead className="w-24">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.slice(0, 50).map((r) => (
                        <TableRow key={r.id} className="cursor-pointer hover:bg-cream-50/80" onClick={() => openPreview(section.key as ArchiveDocType, r)}>
                          <TableCell className="font-medium">{r.patientName ?? "—"}</TableCell>
                          <TableCell className="font-mono text-xs">{r.clinicUhid ?? "—"}</TableCell>
                          <TableCell className="text-xs">{fmtDateTime(r.createdAt)}</TableCell>
                          <TableCell className="text-sm text-muted truncate max-w-md">{r.preview || "—"}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {r.savedToR2 && <Badge variant="outline" className="text-[10px]">R2</Badge>}
                              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openPreview(section.key as ArchiveDocType, r); }}>
                                <FileText size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })}
          {ARCHIVE_SECTIONS.every((s) => ((data[s.key] as ArchiveItem[]) ?? []).length === 0) && (
            <Card>
              <CardContent className="p-12 text-center">
                <FileText size={48} className="mx-auto text-muted" />
                <p className="mt-3 text-muted">No documents in this date range.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {previewType && (
        <ReportPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          type={previewType}
          item={previewItem}
        />
      )}
    </div>
  );
}

function OperationalTab() {
  const [active, setActive] = useState<"daily-collection" | "no-shows" | "upcoming">("daily-collection");

  return (
    <div className="space-y-4">
      <Tabs value={active} onValueChange={(v) => setActive(v as typeof active)}>
        <TabsList>
          <TabsTrigger value="daily-collection">Daily Collection</TabsTrigger>
          <TabsTrigger value="no-shows">No-Shows</TabsTrigger>
          <TabsTrigger value="upcoming">Upcoming Appointments</TabsTrigger>
        </TabsList>
        <TabsContent value="daily-collection"><DailyCollectionTab /></TabsContent>
        <TabsContent value="no-shows"><NoShowsTab /></TabsContent>
        <TabsContent value="upcoming"><UpcomingApptsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function PrintButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  const [busy, setBusy] = useState(false);
  const { isAdmin, permissions } = useAuth();
  const canPrint = isAdmin || permissions.has("reports.print");
  if (!canPrint) return null;
  const run = async () => {
    setBusy(true);
    try { await onClick(); } finally { setBusy(false); }
  };
  return (
    <Button onClick={run} disabled={disabled || busy} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
      {busy ? <LoaderCircle size={16} className="mr-1 animate-spin" /> : <Printer size={16} className="mr-1" />}
      Print PDF
    </Button>
  );
}

function DateRangeBar({ from, to, setFrom, setTo }: { from: string; to: string; setFrom: (v: string) => void; setTo: (v: string) => void }) {
  return (
    <div className="flex items-end gap-3">
      <div>
        <Label className="text-xs">From</Label>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
      </div>
      <div>
        <Label className="text-xs">To</Label>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>
    </div>
  );
}

function DailyCollectionTab() {
  const { from, setFrom, to, setTo } = useDateRangeFilters();
  const { data, isLoading } = useDailyCollection(from, to);
  const rows = data?.rows ?? [];
  const days = data?.days ?? [];
  const grandTotal = days.reduce((s, d) => s + d.totalPaise, 0);

  const print = async () => {
    if (!from || !to) { toast.error("Pick a date range first"); return; }
    const doc = await createDailyCollectionPdf({
      from, to, rows,
      days: days.map((d) => ({ day: d.day, totalPaise: d.totalPaise, count: d.count })),
    });
    doc.save(`daily-collection-${from}-to-${to}.pdf`);
    toast.success("PDF generated");
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-4 flex items-end justify-between flex-wrap gap-3">
          <DateRangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />
          <PrintButton onClick={print} disabled={isLoading || rows.length === 0} />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>UHID</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Method</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={7}><Skeleton className="h-10" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted py-8">No payments in this range</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{r.number}</TableCell>
                  <TableCell>{r.patientName ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{r.clinicUhid ?? "—"}</TableCell>
                  <TableCell className="text-xs">{fmtDate(r.date)}</TableCell>
                  <TableCell className="text-xs">{r.createdAt ? fmtTime(r.createdAt) : "—"}</TableCell>
                  <TableCell>{r.method ?? "Cash"}</TableCell>
                  <TableCell className="text-right font-medium">{fmtINR(r.paidPaise)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            {days.length > 0 && (
              <tfoot className="border-t border-cream-200 bg-cream-50 font-medium">
                {days.map((d) => (
                  <tr key={d.day}>
                    <td colSpan={6} className="p-3 text-right text-maroon-700">{fmtDate(d.day)} ({d.count} payments):</td>
                    <td className="p-3 text-right text-maroon-700">{fmtINR(d.totalPaise)}</td>
                  </tr>
                ))}
                <tr className="bg-maroon-800 text-cream-50">
                  <td colSpan={6} className="p-3 text-right">Grand Total:</td>
                  <td className="p-3 text-right">{fmtINR(grandTotal)}</td>
                </tr>
              </tfoot>
            )}
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function NoShowsTab() {
  const { from, setFrom, to, setTo } = useDateRangeFilters();
  const { data, isLoading } = useNoShows(from, to);
  const rows = data?.rows ?? [];

  const print = async () => {
    if (!from || !to) { toast.error("Pick a date range first"); return; }
    const doc = await createNoShowsPdf({ from, to, rows });
    doc.save(`no-shows-${from}-to-${to}.pdf`);
    toast.success("PDF generated");
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-4 flex items-end justify-between flex-wrap gap-3">
          <DateRangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />
          <PrintButton onClick={print} disabled={isLoading} />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Slot</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>UHID</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={6}><Skeleton className="h-10" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted py-8">No no-shows in this range</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{r.arrivalTime ? fmtDate(r.arrivalTime) : "—"}</TableCell>
                  <TableCell>{r.slot ?? "—"}</TableCell>
                  <TableCell>{r.patientName ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{r.clinicUhid ?? "—"}</TableCell>
                  <TableCell>{r.phone ?? "—"}</TableCell>
                  <TableCell className="text-sm text-muted">{r.reason ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function UpcomingApptsTab() {
  // Lazy initializers keep the clock read out of render (purity).
  const [from, setFrom] = useState(() => dateIST());
  const [to, setTo] = useState(() => dateIST(Date.now() + 7 * 24 * 3600 * 1000));
  const { data, isLoading } = useUpcomingAppts(from, to);
  const rows = data?.rows ?? [];

  const print = async () => {
    if (!from || !to) { toast.error("Pick a date range first"); return; }
    const doc = await createUpcomingApptsPdf({ from, to, rows });
    doc.save(`upcoming-appts-${from}-to-${to}.pdf`);
    toast.success("PDF generated");
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-4 flex items-end justify-between flex-wrap gap-3">
          <DateRangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />
          <PrintButton onClick={print} disabled={isLoading} />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Slot</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>UHID</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Doctor</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8}><Skeleton className="h-10" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted py-8">No upcoming appointments</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{r.arrivalTime ? fmtDate(r.arrivalTime) : "—"}</TableCell>
                  <TableCell className="text-xs">{r.arrivalTime ? fmtTime(r.arrivalTime) : "—"}</TableCell>
                  <TableCell>{r.slot ?? "—"}</TableCell>
                  <TableCell>{r.patientName ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{r.clinicUhid ?? "—"}</TableCell>
                  <TableCell>{r.phone ?? "—"}</TableCell>
                  <TableCell>{r.doctorName ?? "—"}</TableCell>
                  <TableCell><Badge variant="outline">{r.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
